import React, { ReactElement, useEffect, useState } from 'react'

import PageHeader from '@instabox/internal-web-ui/components/PageHeader'
import PageLoading from '@instabox/internal-web-ui/components/PageLoading'
import Table from '@instabox/internal-web-ui/components/Table'
import styles from '@instabox/internal-web-ui/components/Table/table.module.scss'
import { Device } from '@instabox/lockers-update-common/types'
import { formatISO9075 } from 'date-fns'
import { useParams } from 'react-router-dom'

import fetch from '../fetch'

import PackageVersionDetailsButton from './components/PackageVersionDetailsButton'
import TableActionsContainer from './components/TableActionsContainer'

interface DeviceParams {
  deviceId: string
}

function DeviceView(): ReactElement {
  const { deviceId }: DeviceParams = useParams()
  const [device, setDevice] = useState<Device>()
  const [loading, setLoading] = useState<boolean>(false)

  useEffect(() => {
    const getDevice = async (): Promise<void> => {
      setLoading(true)

      try {
        const response = await fetch(`/api/device/${deviceId}`)

        if (!response.ok) {
          throw new Error(`Unable to get packages: ${response.status} ${response.statusText}`)
        }

        const data = await response.json()

        setDevice(data)
      } finally {
        setLoading(false)
      }
    }

    getDevice().catch(console.error)
  }, [deviceId])

  if (loading || device === undefined) {
    return <PageLoading title="" type="page"></PageLoading>
  }

  return (
    <div>
      <PageHeader title={device.deviceName ?? ''} />

      <PageHeader title="Device info" size="md" />

      <div className={`${styles.table_wrapper} mb-4`}>
        <table className={styles.table}>
          <tbody>
            <tr>
              <td>Locker ID</td>
              <td>{device.deviceId}</td>
            </tr>
            <tr>
              <td>Locker short ID</td>
              <td>{device.deviceName}</td>
            </tr>
            <tr>
              <td>Last updated at</td>
              <td>{formatISO9075(new Date(device.updatedAt as Date))}</td>
            </tr>
            <tr>
              <td>OS name</td>
              <td>{device.osInfo?.name}</td>
            </tr>
            <tr>
              <td>OS ID</td>
              <td>{device.osInfo?.id}</td>
            </tr>
            <tr>
              <td>OS Version</td>
              <td>{device.osInfo?.version}</td>
            </tr>
            <tr>
              <td>OS Version ID</td>
              <td>{device.osInfo?.versionId}</td>
            </tr>
          </tbody>
        </table>
      </div>

      <PageHeader title="Installed packages" size="md" />

      <Table
        isLoading={loading}
        headers={{
          name: 'Package name',
          version: 'Version',
          actions: '',
        }}
        rows={device.packages.map((pkg) => ({
          ...pkg,
          actions: (
            <TableActionsContainer>
              <PackageVersionDetailsButton version={pkg.version} name={pkg.name} />
            </TableActionsContainer>
          ),
        }))}
      />
    </div>
  )
}

export default DeviceView
